import * as i0 from '@angular/core';
import { Injectable, Optional, Inject } from '@angular/core';
import { OPT_WINDOW } from '@slidebean/hydra/web-apis';
import * as i1 from '@slidebean/hydra/platform';
import { Subject, combineLatest, concat, Observable } from 'rxjs';
import { take, skip, debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { coerceArray } from '@slidebean/hydra/coercion';

// taken from: https://github.com/angular/components/blob/2defaf7dc91c4257691891c3ee5a6979b4ac5de7/src/cdk/layout/media-matcher.ts
const mediaQueriesForWebkitCompatibility = new Set();
let mediaQueryStyleNode;
// For Webkit engines that only trigger the MediaQueryListListener when
// there is at least one CSS selector for the respective media query.
function createEmptyStyleRule(query) {
    if (mediaQueriesForWebkitCompatibility.has(query)) {
        return;
    }
    try {
        if (!mediaQueryStyleNode) {
            mediaQueryStyleNode = document.createElement('style');
            mediaQueryStyleNode.setAttribute('type', 'text/css');
            document.head.appendChild(mediaQueryStyleNode);
        }
        if (mediaQueryStyleNode.sheet) {
            mediaQueryStyleNode.sheet.insertRule(`@media ${query} {.fx-query-test{ }}`, 0);
            mediaQueriesForWebkitCompatibility.add(query);
        }
    }
    catch (e) {
        console.error(e);
    }
}
function noopMatchMedia(query) {
    // Use `as any` here to avoid adding additional necessary properties for
    // the noop matcher.
    return {
        matches: query === 'all' || query === '',
        media: query,
        addListener: () => {
            return void 0;
        },
        removeListener: () => {
            return void 0;
        },
    };
}
/** Allows to run media queries and match them against the document. */
class MediaMatcher {
    constructor(platform, window) {
        this.platform = platform;
        this.window = window;
        this.matchMedia =
            this.platform.isBrowser && !!this.window?.matchMedia
                ? // matchMedia is bound to the window scope intentionally as it is an illegal invocation to
                    // call it from a different scope.
                    this.window.matchMedia.bind(this.window)
                : noopMatchMedia;
    }
    /**
     * Determines if the document matches the provided media query.
     * @param query media query to be matched.
     * @returns a `MediaQueryList` that can be used to detect matches and monitor for changes to those matches over time.
     */
    match(query) {
        if (this.platform.WEBKIT)
            createEmptyStyleRule(query);
        return this.matchMedia(query);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MediaMatcher, deps: [{ token: i1.Platform }, { token: OPT_WINDOW, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MediaMatcher, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MediaMatcher, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.Platform }, { type: Window, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [OPT_WINDOW]
                }] }] });

// taken from: https://github.com/angular/components/blob/2defaf7dc91c4257691891c3ee5a6979b4ac5de7/src/cdk/layout/breakpoints-observer.ts
function splitQueries(queries) {
    return queries
        .map((query) => query.split(','))
        .reduce((a1, a2) => a1.concat(a2))
        .map(query => query.trim());
}
/** Allows to check the matching state of `@media` queries. */
class BreakpointObserver {
    constructor(media, zone) {
        this.media = media;
        this.zone = zone;
        this.queries = new Map();
        this.destroy$ = new Subject();
    }
    /**
     * Determines whether one or more media queries match the current viewport size.
     * @param value One or more media queries to check.
     * @returns Whether any of the media queries match.
     */
    isMatched(value) {
        return splitQueries(coerceArray(value)).some(query => this.registerQuery(query).mql.matches);
    }
    /**
     * Gets an observable of results for the given queries that will emit new results for any changes
     * in matching of the given queries.
     * @param value One or more media queries to check.
     * @returns A stream of matches for the given queries.
     */
    observe(value) {
        const queries = splitQueries(coerceArray(value));
        const observables$ = queries.map(query => this.registerQuery(query).observable$);
        let state$ = combineLatest(observables$);
        // Emit the first state immediately, and then debounce the subsequent emissions.
        state$ = concat(state$.pipe(take(1)), state$.pipe(skip(1), debounceTime(0)));
        return state$.pipe(map(states => {
            const response = { matches: false, breakpoints: {} };
            states.forEach(state => {
                response.matches = response.matches || state.matches;
                response.breakpoints[state.query] = state.matches;
            });
            return response;
        }));
    }
    registerQuery(query) {
        // Only set up a new MediaQueryList if it is not already being listened for.
        if (this.queries.has(query))
            return this.queries.get(query);
        const mql = this.media.match(query);
        // Create callback for match changes and add it is as a listener.
        const observable$ = new Observable((observer) => {
            // Listener callback methods are wrapped to be placed back in ngZone. Callbacks must be placed
            // back into the zone because matchMedia is only included in Zone.js by loading the
            // webapis-media-query.js file alongside the zone.js file.  Additionally, some browsers do not
            // have MediaQueryList inherit from EventTarget, which causes inconsistencies in how Zone.js
            // patches it.
            const handler = (e) => this.zone.run(() => observer.next(e));
            !!mql.addEventListener
                ? mql.addEventListener('change', handler)
                : mql.addListener(handler);
            return () => !!mql.removeEventListener
                ? mql.removeEventListener('change', handler)
                : mql.removeListener(handler);
        }).pipe(startWith(mql), map((next) => ({ query, matches: next.matches })), takeUntil(this.destroy$));
        const output = { observable$, mql };
        this.queries.set(query, output);
        return output;
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BreakpointObserver, deps: [{ token: MediaMatcher }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BreakpointObserver, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BreakpointObserver, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: MediaMatcher }, { type: i0.NgZone }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BreakpointObserver, MediaMatcher };
